import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { OverlayEventDetail } from "@ionic/core/components";
import {
    IonAvatar,
    IonButton,
    IonCard,
    IonCardSubtitle,
    IonImg,
    IonSkeletonText,
    IonSpinner,
    useIonAlert,
    useIonModal,
    useIonToast
} from "@ionic/react";
import { alertCircleOutline, chatbubbleEllipsesOutline } from "ionicons/icons";
import React, { memo, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useAnalytics } from '../../../AppAnalyticsProvider';
import useAuthService from "../../../hooks/useAuthService";
import { useRevenueCatPaywall } from "../../../hooks/useRevenueCatPaywall";
import { openNativeMapWithLocation, resolveLocationCoordinates, useTrickLocation } from '../../../hooks/useTrickLocation';
import { TrickMetadataStats } from "../../../models/trickMetadataStats";
import { UserTrickSearch } from "../../../models/user-trick-search";
import { UserTrickSearchMetadata } from "../../../models/userTrickSearchMetadata";
import TricksService from "../../../services/tricks.service";
import { handleTagClickUtil, splitString } from "../../../services/utils";
import BiskHome from "../../ComponentsBisk/BiskHome/BiskHome";
import CommentModal from "../../ComponentsComments/CommentModal/CommentModal";
import TrickCommentsCount from "../../ComponentsComments/TrickCommentsCount/TrickCommentsCount";
import ReportReason from "../../ComponentsProfile/ReportReason/ReportReason";
import TrickShare from "../../ComponentsShare/TrickShare/TrickShare";
import HomePageVideoPlayer from "../../ComponentsVideo/HomePageVideo/HomePageVideo";
import IDXButtonIcon from "../IDXButtonIcon/IDXButtonIcon";
import TrickCardExtraPopover from "../TrickCardExtraPopover/TrickCardExtraPopover";
import TrickLike from "../TrickLike/TrickLike";
import TrickSaveComponent from "../TrickSaveComponent/TrickSaveComponent";
import { usePhoneVerification } from "../../../PhoneVerificationProvider";
import UsernameDisplay from "../Username/UsernameDisplay";
import VideoTagDisplay from "../VideoTagDisplay/VideoTagDisplay";

// Full card skeleton shown while video properties are loading
const CardSkeleton: React.FC = () => (
    <div className="py-2 xs:h-full xs:w-full sm:w-10/12 sm:h-10/12 md:w-7/12 md:h-7/12 l:w-5/12 l:h-5/12 xl:w-6/12 xl:h-6/12">
        <div className="bg-transparent" style={{ margin: 0, padding: 0 }}>
            {/* Header skeleton */}
            <div className="flex flex-row justify-between items-center mb-2">
                <div className="flex flex-row items-center">
                    <div className="mx-4">
                        <IonSkeletonText animated style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                    </div>
                    <div className="flex flex-col gap-y-1">
                        <IonSkeletonText animated style={{ width: '120px', height: '20px' }} />
                        <IonSkeletonText animated style={{ width: '80px', height: '12px' }} />
                    </div>
                </div>
                <div className="mr-4">
                    <IonSkeletonText animated style={{ width: '24px', height: '24px' }} />
                </div>
            </div>

            {/* Video placeholder - matches vertical video aspect ratio */}
            <div className="relative overflow-hidden rounded-lg mx-2" style={{ aspectRatio: '9/16', backgroundColor: '#18181b' }}>
                <div className="absolute inset-0 flex justify-center items-center">
                    <IonSpinner name="crescent" color="medium" style={{ transform: 'scale(1.5)' }} />
                </div>
            </div>

            {/* Title and description skeleton */}
            <div className="flex flex-col pl-4 pr-4 my-2 gap-y-1">
                <IonSkeletonText animated style={{ width: '60%', height: '24px' }} />
                <IonSkeletonText animated style={{ width: '90%', height: '16px' }} />
            </div>

            {/* Action buttons skeleton */}
            <div className="flex flex-row justify-between mb-2 mx-2 px-2">
                <div className="flex flex-row items-center gap-x-4">
                    <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                    <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                    <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                    <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                </div>
                <div className="flex flex-row gap-x-2">
                    <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                    <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                </div>
            </div>

            {/* Tags skeleton */}
            <div className="flex flex-row gap-x-2 px-4 flex-wrap">
                <IonSkeletonText animated style={{ width: '70px', height: '28px', borderRadius: '14px' }} />
                <IonSkeletonText animated style={{ width: '50px', height: '28px', borderRadius: '14px' }} />
                <IonSkeletonText animated style={{ width: '60px', height: '28px', borderRadius: '14px' }} />
            </div>
        </div>
    </div>
);

interface ContainerProps {
    trick: UserTrickSearchMetadata;
    isAuthed: boolean;
    userId: string,
    isCapacitor: boolean;
    userType: string;
    publicId: string;
    publicBisk: number;
    publicUsername: string;
    publicIsSubscribed: boolean;
    scrollSpeed?: number;
    isScrolling?: boolean;
}

const HomeTrickCard: React.FC<ContainerProps> = memo((props: ContainerProps) => {
    const history = useHistory();
    const { login } = useAuthService();
    const { trackEvent } = useAnalytics();
    const { requirePhone } = usePhoneVerification();

    // React Query hook for trick location data
    const { data: trickLocationData, isLoading: isLoadingLocation, error: locationError } = useTrickLocation(
        props.trick.id,
        // Only fetch if we have location text but want to get coordinates
        true
    );

    const [presentAlert] = useIonAlert();
    const [profilePicSrc, setProfilePicSrc] = React.useState<string>('/assets/photos/defaultProPic.png');
    const [isHorizontal, setIsHorizontal] = useState<boolean>(undefined);
    const [isExternal, setIsExternal] = useState<boolean>(false);
    const [isYoutube, setIsYoutube] = useState<boolean>(false);
    const [isVimeo, setIsVimeo] = useState<boolean>(false);
    const [isAirtime, setIsAirtime] = useState<boolean>(false);
    const [aspectRatio, setAspectRatio] = useState<number>(16 / 9);
    const [present] = useIonToast();

    const [reportOpen, setReportOpen] = useState(false);

    // Working on implementation - tbd if actually need to do
    const [isCached, setIsCached] = useState<boolean>(false);

    const [trickMetadataStats, setTrickMetadataStats] = useState<undefined | TrickMetadataStats>(undefined);

    const {
        presentPaywall: presentTowRopePaywall,
    } = useRevenueCatPaywall({
        userId: props.publicId,
        userBisk: props.publicBisk,
        username: props.publicUsername
    });

    const onClickPersonComments = (username: string) => {
        trackEvent('trick_card_comment_user_click', {
            username,
            trick_id: props.trick.id
        });
        history.push("/profile/" + username);
    }

    const getTrickMetadataStats = async (trickId: string) => {
        const response: TrickMetadataStats = await TricksService.getTrickInteractionsCount(trickId);
        if (response) {
            setTrickMetadataStats(response);
        }
    }

    const [presentComments, dismissComments] = useIonModal(CommentModal, {
        onDismiss: (data: string, role: string) => dismissComments(data, role),
        trickId: props.trick.id,
        isAuthed: props.isAuthed,
        userType: props.userType,
        publicId: props.publicId,
        onClickResult: onClickPersonComments
    })

    const getVideoProperties = (trick: UserTrickSearchMetadata) => {
        if (trick === undefined) {
            return;
        }

        if (trick.object_id === undefined || trick.object_id === "" || trick.object_id === null) {
            setIsHorizontal(false);
            return;
        } else {
            setIsHorizontal(!trick.is_vertical);
            setIsExternal(trick.is_external);
            setIsYoutube(trick.is_youtube);
            setIsVimeo(trick.is_vimeo);
            setIsAirtime(trick.is_airtime);
            setAspectRatio(trick.aspect_ratio);
        }
    }

    useEffect(() => {
        getVideoProperties(props.trick)
        getProfilePic(props.trick);
        setIsCached(false);
    }, [props.trick]);

    useEffect(() => {
        if (props.trick.id !== "" && props.trick.id !== undefined && props.trick.id !== null) {
            getTrickMetadataStats(props.trick.id);
        }
    }, [props.trick.id]);

    const presentToastLoginHandler = async (message: string) => {
        trackEvent('auth_prompt_toast', {
            message,
            feature: 'follow_user' // or whatever feature triggered this
        });

        await present({
            message: message,
            duration: 3000,
            position: 'top',
            icon: alertCircleOutline,
            buttons: [
                {
                    text: 'Login',
                    handler: async () => {
                        trackEvent('auth_prompt_action', {
                            message,
                            action: 'login_click'
                        });
                        await login();
                    }
                },
                {
                    text: 'Cancel',
                    role: 'cancel',
                    handler: () => {
                        trackEvent('auth_prompt_action', {
                            message,
                            action: 'cancel_click'
                        });
                    }
                }
            ]
        });
    };

    const handleTagClick = async (tag: string) => {
        const newTag = handleTagClickUtil(tag);
        await Haptics.impact({ style: ImpactStyle.Light });
        trackEvent('trick_card_tag_click', {
            tag: newTag,
            trick_id: props.trick.id,
            user_id: props.trick.user_id
        });
        history.push('/search/' + newTag);
    }

    const handleUserTagClick = async (username: string) => {
        await Haptics.impact({ style: ImpactStyle.Light });
        trackEvent('trick_video_tag_click', {
            trick_id: props.trick.id,
            tag: username,
            user_id: props.trick.user_id
        });
        history.push('/profile/' + username);
    };

    const onClickCard = async (user_id: string, trick_id: string) => {
        if (!props.isAuthed) {
            await presentToastLoginHandler("Login to view clip details");
            trackEvent('trick_card_auth_prompt', {
                trick_id,
                user_id,
                action: 'view_details'
            });
            return;
        }

        requirePhone(() => {
            trackEvent('trick_card_click', {
                trick_id,
                user_id,
                trick_name: props.trick.trick_name
            });
            history.push('/clip/' + user_id + '/' + trick_id);
        });
    }

    const onClickHeader = async (username: string) => {
        if (!props.isAuthed) {
            await presentToastLoginHandler("You must be logged in to view profiles!");
            trackEvent('trick_card_auth_prompt', {
                username,
                action: 'view_profile'
            });
            return;
        } else {
            requirePhone(() => {
                trackEvent('trick_card_profile_click', {
                    username,
                    viewer_id: props.publicId
                });
                history.push('/profile/' + username);
            });
        }
    }

    const getProfilePic = (trick: UserTrickSearch) => {
        const userProfilePic = trick.profile_pic;
        if (userProfilePic === "" || userProfilePic === undefined || userProfilePic === null) {
            setProfilePicSrc('/assets/photos/defaultProPic.png');
        } else {
            setProfilePicSrc(userProfilePic);
        }
    }

    const addTaggedUserToComments = (comment: string) => {
        const parsedComment = splitString(comment, /(\@[A-Za-z0-9_.-]+)/);
        return (
            <>
                {parsedComment.map((phrase, index) => {
                    if (phrase.at(0) === '@') {
                        return (
                            <span
                                className="text-indigo-300 cursor-pointer"
                                onClick={async () => {
                                    onClickPersonComments(phrase.substring(1));
                                }}
                                key={index}
                            >
                                {phrase}
                            </span>
                        );
                    } else {
                        return <span key={index}>{phrase}</span>;
                    }
                })}
            </>
        );
    }

    const handleTrickLocationClick = async (location: string) => {
        // For Capacitor apps, determine coordinates and open native map
        trackEvent('trick_card_location_click', {
            location,
            trick_id: props.trick.id
        });

        if (props.isCapacitor) {
            if (props.publicIsSubscribed) {
                return await openNativeMapWithLocation(location, trickLocationData, props.trick.thumbnail);
            } else {
                return await presentTowRopePaywall();
            }
        }

        // For web apps, resolve coordinates and navigate
        const { lat, lng } = await resolveLocationCoordinates(location, trickLocationData);
        history.push(`/map?lat=${lat}&long=${lng}`);
    }

    const openTrickCommentsModal = async () => {
        if (props.isAuthed) {
            requirePhone(() => {
                Haptics.impact({ style: ImpactStyle.Light });
                trackEvent('trick_card_comments_open', {
                    trick_id: props.trick.id,
                    user_id: props.trick.user_id,
                    comment_count: trickMetadataStats?.total_comment_count
                });
                presentComments({
                    onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                        trackEvent('trick_card_comments_close', {
                            trick_id: props.trick.id,
                        });
                    },
                });
            });
        } else {
            await presentToastLoginHandler("You must be logged in to view comments!");
            trackEvent('trick_card_auth_prompt', {
                trick_id: props.trick.id,
                action: 'view_comments'
            });
        }
    }

    const handleIsChildOpen = (isOpen: boolean) => {
        setReportOpen(isOpen);
    }

    const handleReportClick = () => {
        trackEvent('trick_card_report_click', {
            trick_id: props.trick.id,
            reported_user_id: props.trick.user_id,
            reporter_id: props.publicId
        });
        setReportOpen(true);
    }

    // Track initial card view
    useEffect(() => {
        if (props.trick?.id) {
            trackEvent('trick_card_view', {
                trick_id: props.trick.id,
                user_id: props.trick.user_id,
                trick_name: props.trick.trick_name,
                viewer_id: props.publicId
            });
        }
    }, [props.trick?.id]);

    // Track metadata stats updates
    useEffect(() => {
        if (trickMetadataStats) {
            trackEvent('trick_card_engagement_stats', {
                trick_id: props.trick.id,
                votes: trickMetadataStats.votes,
                comments: trickMetadataStats.total_comment_count
            });
        }
    }, [trickMetadataStats]);

    // Debug: Track location data availability
    useEffect(() => {
        if (trickLocationData && props.trick.location) {
            console.log(`Location data loaded for trick ${props.trick.id}:`, {
                location_text: props.trick.location,
                coordinates: `${trickLocationData.google_lat},${trickLocationData.google_long}`,
                name: trickLocationData.name
            });
        }
    }, [trickLocationData, props.trick.location, props.trick.id]);

    // Show skeleton while video properties are loading
    if (isHorizontal === undefined) {
        return <CardSkeleton />;
    }

    return (
        <div key={props.trick.id}
            className="py-2 xs:h-full xs:w-full sm:w-10/12 sm:h-10/12 md:w-7/12 md:h-7/12 l:w-5/12 l:h-5/12 xl:w-6/12 xl:h-6/12">
            <IonCard className="bg-transparent border-0" style={{ margin: 0, padding: 0 }}>
                <div className="flex flex-row justify-between items-center">
                    <div className={`flex flex-row cursor-pointer ${(props.trick?.location === undefined || props.trick?.location === "") ? 'items-center' : 'items-start'}`}>
                        <IonAvatar slot="start" className="mx-4"
                            onClick={() => onClickHeader(props.trick?.username)}>
                            <IonImg className="shrink w-12 h-12 rounded-full" src={profilePicSrc}></IonImg>
                        </IonAvatar>
                        <div className="flex flex-col gap-y-0.5">
                            <UsernameDisplay username={props.trick?.username}
                                className={"text-xl font-bold text-zinc-50"}
                                onUsernameClickHandler={onClickHeader} userId={props.trick?.user_id}
                                loggedInId={props.publicId}></UsernameDisplay>
                            {(props.trick?.location !== undefined && props.trick?.location !== "") ?
                                <IonCardSubtitle
                                    className={`text-xs text-zinc-300 ${isLoadingLocation ? 'opacity-70' : 'cursor-pointer'}`}
                                    onClick={() => !isLoadingLocation && handleTrickLocationClick(props.trick.location)}
                                >
                                    {props.trick.location}
                                    {isLoadingLocation && <span className="ml-1">⋯</span>}
                                </IonCardSubtitle> : null}
                        </div>
                    </div>
                    <ReportReason isOpenParent={reportOpen}
                        text={"User ID: " + props.publicId + " wishes to block " + props.trick.username + " with trick id " + props.trick.id}
                        usernameToReport={props.trick.username} reportingUsername={props.publicId}
                        objectIdToReport={props.trick.id} reportingId={props.publicId}
                        reportedId={props.trick.user_id}
                        handleIsOpen={handleIsChildOpen} />
                    <TrickCardExtraPopover isCapacitor={props.isCapacitor} trick={props.trick} isAuthed={props.isAuthed}
                        userId={props.userId} trickId={props.trick.id} publicId={props.publicId}
                        userType={props.userType} displayTrickShare={false}
                        displayTrickInstagramShare={true} displayTrickDownload={true}
                        userOnDisplayUsername={props.trick?.username}
                        onReportTrick={handleReportClick}></TrickCardExtraPopover>
                </div>

                <HomePageVideoPlayer
                    thumbnail={props.trick.thumbnail}
                    src={props.trick.video}
                    id={props.trick.id}
                    isCapacitor={props.isCapacitor}
                    isHorizontal={isHorizontal}
                    isCached={false}
                    isExternal={isExternal}
                    isYoutube={isYoutube}
                    isVimeo={isVimeo}
                    isAirtime={isAirtime}
                    aspectRatio={aspectRatio}
                    isScrolling={props.isScrolling}
                    scrollSpeed={props.scrollSpeed}
                />
                <div className="flex flex-col pl-4 pr-4 my-2 gap-y-1">
                    <div className="text-xl font-bold text-zinc-50"
                        onClick={() => onClickCard(props.trick.user_id, props.trick.id)}>{props.trick.trick_name}</div>
                    {props.trick?.description ?
                        <div className="text-sm text-zinc-300">
                            <div>{addTaggedUserToComments(props?.trick?.description)}</div>
                        </div> : null
                    }
                </div>

                <div className="flex flex-row justify-between mb-2 mx-2">
                    <div className="flex flex-row items-center gap-x-2">
                        <TrickLike trickId={props.trick.id} isAuthed={props.isAuthed} publicId={props.publicId}
                            userType={props.userType} count={trickMetadataStats?.votes} />
                        <IonButton fill="clear" size="small" color="dark" onClick={() => openTrickCommentsModal()}>
                            <IDXButtonIcon size="text-2xl" icon={chatbubbleEllipsesOutline} />
                        </IonButton>
                        <TrickCommentsCount trickId={props.trick.id} isAuthed={props.isAuthed} userType={props.userType}
                            publicId={props.publicId} onClickResult={onClickPersonComments}
                            count={trickMetadataStats?.total_comment_count} />
                        <BiskHome senderId={props.publicId} isAuthed={props.isAuthed} recipientId={props.trick?.user_id}
                            trickId={props.trick?.trick_id} trickName={props.trick?.trick_name}
                            recipientUsername={props.trick?.username} />
                    </div>
                    <div className={"flex flex-row space-x-0.5 items-center"}>
                        <TrickShare isCapacitor={props.isCapacitor} trickId={props.trick.id} trick={props.trick}
                            list={false} username={props.trick.username}></TrickShare>
                        <TrickSaveComponent isAuthed={props.isAuthed} publicId={props.publicId} trickId={props.trick.id}
                            userType={props.userType} />
                    </div>
                </div>
                <div className="">
                    <VideoTagDisplay
                        taggedUsers={props.trick?.tagged_users}
                        handleUserTagClick={handleUserTagClick}
                        tags={props.trick?.tags}
                        isAuthed={props.isAuthed}
                        handleTagClick={handleTagClick}
                        trick={props.trick}></VideoTagDisplay>
                </div>
            </IonCard>
        </div>
    )
});

export default HomeTrickCard;